import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, FileText, Filter, Loader2, ArrowLeft, ShieldAlert } from 'lucide-react';
import * as XLSX from 'xlsx';

// =====================================================================
// Consulta das visitas da Avaliação de Segurança do Alimento (ex-Auditoria
// SA — DEM-00011, Fase 4, T22).
//
// Pedido da Julia (2026-09-23): padronizar com a Avaliação 360 e a
// Autoverificação de Fechamento Mensal — LISTA PLANA, filtros AO VIVO
// (client-side), legenda das faixas, linha clicável, export Excel no
// navegador. Substitui `AvaliacaoSARelatorio.js` (que delegava para o
// `RelatorioFormulario.js` compartilhado com a TSV) SÓ PARA A SA — a TSV
// continua com a tela antiga, em Treinamento; não foi pedido mexer nela.
//
// ⚠️ O DETALHE NÃO reabre pergunta a pergunta — decisão já existente antes
// desta tela (ver o cabeçalho antigo de RelatorioFormulario.js): "o detalhe
// pergunta a pergunta virou o PDF", que traz também as fotos das negativas.
// Reconstituir isso aqui exigiria extrair o texto de ~36 perguntas de dentro
// do questionário (arquivo de preenchimento, não de leitura) só para repetir
// o que o PDF já mostra melhor (com foto). O detalhe aqui é o RESUMO POR
// ÁREA — o que permite comparar lojas, que é o que esta tela existe pra
// fazer — e um botão para abrir o PDF.
//
// ⚠️ DUAS SÉRIES CONVIVEM (DEM-00011, T15/T18): o marcador `METODO_NOTA`
// (join com dtbFormularioMetodo) diz se a visita é do método novo
// (CONTAGEM — régua Verde/Amarelo/Laranja/Vermelho) ou do legado (peso,
// SEM classificação por cor — não há e não pode haver coluna CLASSIFICACAO
// nestas tabelas, teto do InnoDB). Legado aparece como "Método antigo",
// sem cor, no lugar de inventar uma classificação que não existe.
//
// ⚠️ A SA tem VETO, a 360 não: STATUS (APROVADA/IMPUR) é um eixo à parte da
// cor — nota alta com questão crítica reprovada ainda é IMPUR. O servidor já
// manda a causa pronta em MOTIVO_IMPUR (T20) — esta tela só exibe.
// =====================================================================

const API_BASE = '/api/auditoria';
const COR = '#475569'; // ardósia do módulo Operacional
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// As 7 questões CRÍTICAS da Avaliação de Segurança do Alimento: uma resposta
// "Não" em qualquer uma delas veta a avaliação para IMPUR, por mais alta que
// seja a nota (é o que faz um 96% reprovar). Aqui a lista é só LEGENDA para a
// leitura — a fonte da verdade do veto é o servidor (server/treinamento/
// formularios/server-auditoriasa.js › `criticas`), que já calcula STATUS e
// MOTIVO_IMPUR. Mantidas em sincronia com aquela lista.
const CRITICAS_SA = ['FS1', 'FS2', 'FS3', 'FS6', 'FS7', 'FS8', 'FS2026_01'];

// Seções pontuadas da SA (mesma fonte de AvaliacaoSARelatorio.js — CSV e
// aqui precisam concordar sobre quais perguntas formam cada área).
// ⚠️ Só as PONTUADAS — as críticas e HST não formam área aqui de propósito:
// a causa do IMPUR já vem pronta em MOTIVO_IMPUR (T20), então esta tela não
// precisa nomear qual crítica reprovou; quem quiser vê-la no PDF.
const SECOES = [
  { key: 'higiene', label: 'Higiene e Limpeza', chaves: ['FS9', 'FS10', 'FS11', 'FS12', 'FS13'] },
  { key: 'prevencao', label: 'Prevenção contra Contaminação', chaves: ['FS14', 'FS15', 'FS16', 'FS17', 'FS18', 'FS19', 'FS20', 'FS21', 'FS22'] },
  { key: 'armazenamento', label: 'Armazenamento', chaves: ['FS23', 'FS24', 'FS25', 'FS26'] },
  { key: 'culinaria', label: 'Culinária', chaves: ['FS27'] },
  { key: 'geral', label: 'Geral', chaves: ['FS28', 'FS29', 'FS30', 'FS31', 'FS32', 'FS33'] },
];

// Régua NOVA (só para o método CONTAGEM) — espelha aval360-score.js.
const corClassificacao = (c) => {
  if (c === 'Verde')    return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Verde' };
  if (c === 'Amarelo')  return { bg: '#fef9c3', color: '#854d0e', icone: '🟡', rotulo: 'Amarelo' };
  if (c === 'Laranja')  return { bg: '#ffedd5', color: '#9a3412', icone: '🟠', rotulo: 'Laranja' };
  if (c === 'Vermelho') return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Vermelho' };
  return { bg: '#f1f5f9', color: '#475569', icone: '', rotulo: c || '-' };
};
const FAIXAS_LEGENDA = [
  { classif: 'Verde', faixa: '90 a 100%' },
  { classif: 'Amarelo', faixa: '80 a 89,99%' },
  { classif: 'Laranja', faixa: '70 a 79,99%' },
  { classif: 'Vermelho', faixa: 'abaixo de 70%' },
];

// Classificação por percentual — SÓ para o método novo (espelha o
// `faixaPorPercentual` que RelatorioFormulario.js já usava). O legado não
// tem classificação: não há coluna, e não é este componente que vai inventar
// uma fórmula nova para dado congelado.
const classificarPorPercentual = (p) => {
  const n = Number(p);
  if (!Number.isFinite(n)) return null;
  if (n >= 90) return 'Verde';
  if (n >= 80) return 'Amarelo';
  if (n >= 70) return 'Laranja';
  return 'Vermelho';
};

const formatarDataBR = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};
const dataRef = (a) => String(a.DATA || '').slice(0, 10);

// Normaliza a resposta ('Sim'/'Não'/'N/A', variações de caixa/acento).
const normalizarResposta = (v) => {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'sim') return 'SIM';
  if (s === 'nao' || s === 'não') return 'NAO';
  return 'NA';
};

// Resumo por área a partir das respostas JÁ NA LINHA (P_<chave>) — contagem
// pura, N/A fora do denominador. Só faz sentido no método CONTAGEM; no
// legado, mostra a contagem crua sem %, porque o denominador de peso não
// está disponível aqui (fixedScores mora no questionário, não na leitura).
function resumoPorArea(registro, metodoNovo) {
  return SECOES.map((secao) => {
    let sim = 0; let nao = 0; let na = 0;
    for (const c of secao.chaves) {
      const r = normalizarResposta(registro[`P_${c}`]);
      if (r === 'SIM') sim++; else if (r === 'NAO') nao++; else na++;
    }
    const aplicavel = sim + nao;
    const percentual = metodoNovo && aplicavel > 0 ? Number(((sim / aplicavel) * 100).toFixed(2)) : null;
    return {
      area: secao.label, sim, nao, na, aplicavel,
      percentual,
      classificacao: metodoNovo ? (aplicavel > 0 ? classificarPorPercentual(percentual) : 'NA') : null,
    };
  });
}

const ITENS_POR_PAGINA = 25;

// `metodo`: quando informado ('PESO' | 'CONTAGEM'), a tela fica PRESA a esse
// método — usado pelas duas abas do card (OperacionalAvaliacaoSA.js), mesmo
// padrão do Checklist (ChecklistHistorico.js › prop `metodo`). `titulo`:
// rótulo do cabeçalho, diferente em cada aba.
const AvaliacaoSAHistorico = ({ acoesExtras, metodo, titulo = 'Avaliações gravadas' }) => {
  const [visitas, setVisitas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [detalheId, setDetalheId] = useState(null);
  const [abrindoPdf, setAbrindoPdf] = useState(null);

  const [fLoja, setFLoja] = useState('');
  const [fMetodo, setFMetodo] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fClassif, setFClassif] = useState('');
  const [fPeriodo, setFPeriodo] = useState('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/forms`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVisitas(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as avaliações.');
      console.error(e);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Classificação (só quando há METODO_NOTA) computada uma vez por linha,
  // reaproveitada no filtro, na coluna e no export.
  const comClassificacao = useMemo(
    () => visitas.map((v) => ({ ...v, _classif: v.METODO_NOTA ? classificarPorPercentual(v.PORCENTAGEM_TOTAL) : null })),
    [visitas]
  );

  // Escopo da aba: com `metodo` fixo, tudo abaixo opera só sobre esse
  // recorte — mesmo raciocínio de ChecklistHistorico.js › `auditoriasDaAba`.
  const visitasDaAba = useMemo(() => {
    if (!metodo) return comClassificacao;
    const querContagem = metodo === 'CONTAGEM';
    return comClassificacao.filter((a) => !!a.METODO_NOTA === querContagem);
  }, [comClassificacao, metodo]);

  const lojas = useMemo(() => [...new Set(visitasDaAba.map((a) => a.LOJA).filter(Boolean))].sort(), [visitasDaAba]);
  // Só os métodos que de fato aparecem NO ESCOPO — com `metodo` fixo isto dá
  // sempre 1 valor, então o seletor se esconde sozinho.
  const metodos = useMemo(() => [...new Set(visitasDaAba.map((a) => (a.METODO_NOTA ? 'CONTAGEM' : 'PESO')))].sort(), [visitasDaAba]);
  const statusList = useMemo(() => [...new Set(visitasDaAba.map((a) => a.STATUS).filter(Boolean))].sort(), [visitasDaAba]);
  const classificacoes = useMemo(() => [...new Set(visitasDaAba.map((a) => a._classif).filter(Boolean))], [visitasDaAba]);

  const filtradas = useMemo(() => visitasDaAba.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fMetodo && (a.METODO_NOTA ? 'CONTAGEM' : 'PESO') !== fMetodo) return false;
    if (fStatus && a.STATUS !== fStatus) return false;
    if (fClassif && a._classif !== fClassif) return false;
    const d = dataRef(a);
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false;
    return true;
  }), [visitasDaAba, fLoja, fMetodo, fStatus, fClassif, fPeriodo]);

  useEffect(() => { setPagina(1); }, [fLoja, fMetodo, fStatus, fClassif, fPeriodo]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhas = filtradas.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);

  const temFiltro = fLoja || fMetodo || fStatus || fClassif || fPeriodo;
  const limparFiltros = () => { setFLoja(''); setFMetodo(''); setFStatus(''); setFClassif(''); setFPeriodo(''); };

  // PDF por fetch (a rota exige token; <a href> daria 401).
  // origem: 'versionado' (série nova, DEM-00014) pede o PDF pela rota versionada
  // (?origem=versionado), gerado sob demanda; 'legado' (ou ausente) segue o
  // caminho antigo. ⚠️ id NÃO é único entre as séries — a origem desambigua.
  const abrirPdf = useCallback(async (id, origem) => {
    setAbrindoPdf(id);
    try {
      const qs = origem === 'versionado' ? '?origem=versionado' : '';
      const r = await fetch(`${API_BASE}/forms/${id}/pdf${qs}`, { headers: auth() });
      if (!r.ok) throw new Error('Não foi possível abrir o PDF desta visita.');
      const url = window.URL.createObjectURL(await r.blob());
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    } finally {
      setAbrindoPdf(null);
    }
  }, []);

  const exportarExcel = () => {
    const dados = filtradas.map((a) => ({
      Loja: a.LOJA, Data: formatarDataBR(a.DATA),
      'Gerente de Negócios': a.GERENTE_NEGOCIO || '', 'Gerente de Plantão': a.GERENTE_PLANTAO || '',
      Método: a.METODO_NOTA ? 'Contagem (novo)' : 'Peso (legado)',
      'Percentual (%)': a.PORCENTAGEM_TOTAL, Classificação: a._classif ? corClassificacao(a._classif).rotulo : 'Método antigo',
      Status: a.STATUS || '', 'Motivo (se IMPUR)': a.MOTIVO_IMPUR || '',
      'Evidências (fotos)': a.QTD_EVIDENCIAS || 0, 'Registrado por': a.REGISTRADO_POR || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dados), 'Avaliações SA');
    XLSX.writeFile(wb, `Avaliacao_Seguranca_Alimento_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (detalheId) {
    // detalheId guarda a LINHA inteira (não o ID): o ID não é único entre as
    // séries legada e versionada, então buscar por ID abriria a linha errada.
    return <DetalheVisita registro={detalheId} onVoltar={() => setDetalheId(null)} abrirPdf={abrirPdf} abrindoPdf={abrindoPdf} />;
  }

  const th = { padding: '13px 16px', fontWeight: 700, borderBottom: '1px solid #e2e8f0', fontSize: 12.5, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const td = { padding: '13px 16px', color: '#334155', fontSize: 14, whiteSpace: 'nowrap' };
  const selStyle = { width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none', background: '#fff', color: '#475569', fontSize: 13 };

  return (
    <div>
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{titulo}</h3>
        {acoesExtras}
        {filtradas.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      <div style={{ backgroundColor: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Loja</label>
          <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        {metodos.length > 1 && (
          <div style={{ flex: '1 1 130px' }}>
            <label style={filtroLbl}>Método</label>
            <select value={fMetodo} onChange={(e) => setFMetodo(e.target.value)} style={selStyle}>
              <option value="">Todos</option>
              {metodos.map((m) => <option key={m} value={m}>{m === 'CONTAGEM' ? 'Contagem' : 'Peso'}</option>)}
            </select>
          </div>
        )}
        {/* Sem sentido na aba Histórico (Peso): não há classificação por cor
            para o método antigo — não há coluna, T18. */}
        {metodo !== 'PESO' && (
          <div style={{ flex: '1 1 150px' }}>
            <label style={filtroLbl}>Classificação</label>
            <select value={fClassif} onChange={(e) => setFClassif(e.target.value)} style={selStyle}>
              <option value="">Todas</option>
              {classificacoes.map((c) => <option key={c} value={c}>{corClassificacao(c).rotulo}</option>)}
            </select>
          </div>
        )}
        <div style={{ flex: '1 1 130px' }}>
          <label style={filtroLbl}>Status</label>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={selStyle}>
            <option value="">Todos</option>
            {statusList.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={filtroLbl}>Período (mm/aaaa)</label>
          <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} />
        </div>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Filter size={15} /> Limpar
          </button>
        )}
      </div>

      {/* Legenda — só a régua nova; o legado não tem classificação (ver cabeçalho). */}
      {/* Com `metodo` fixo, mostra só a legenda relevante daquela aba. */}
      {metodo !== 'PESO' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: metodo ? 16 : 6, paddingLeft: 2 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>Régua (método Contagem)</span>
          {FAIXAS_LEGENDA.map(({ classif, faixa }) => {
            const c = corClassificacao(classif);
            return (
              <span key={classif} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                {c.rotulo}<span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
              </span>
            );
          })}
        </div>
      )}
      {!metodo && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16, paddingLeft: 2 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1f5f9', color: '#475569', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
            Método antigo <span style={{ fontWeight: 600, opacity: 0.85 }}>sem classificação por cor</span>
          </span>
        </div>
      )}

      {erro && <div style={alerta}>{erro}</div>}

      <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={th}>Loja</th>
                <th style={th}>Data</th>
                <th style={th}>G. Negócios</th>
                <th style={th}>G. Plantão</th>
                <th style={{ ...th, textAlign: 'right' }}>Nota</th>
                {metodo !== 'PESO' && <th style={{ ...th, textAlign: 'center' }}>Classificação</th>}
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
                <th style={{ ...th, textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && <tr><td colSpan={metodo === 'PESO' ? 7 : 8} style={{ ...td, textAlign: 'center', color: '#64748b' }}>Carregando…</td></tr>}
              {!carregando && filtradas.length === 0 && (
                <tr><td colSpan={metodo === 'PESO' ? 7 : 8} style={{ ...td, textAlign: 'center', color: '#64748b' }}>
                  {visitasDaAba.length === 0 ? 'Nenhuma avaliação gravada ainda.' : 'Nenhuma avaliação para os filtros escolhidos.'}
                </td></tr>
              )}
              {linhas.map((a) => {
                const reprovada = String(a.STATUS || '').trim() !== 'APROVADA';
                const c = a._classif ? corClassificacao(a._classif) : { bg: '#f1f5f9', color: '#475569', icone: '', rotulo: 'Método antigo' };
                return (
                  <tr key={`${a.ORIGEM || 'leg'}-${a.ID}`} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => setDetalheId(a)}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <td style={{ ...td, fontWeight: 700, color: '#0f172a' }}>{a.LOJA}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{formatarDataBR(a.DATA) || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_NEGOCIO || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_PLANTAO || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ fontWeight: 800, color: a._classif ? c.color : '#334155', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(a.PORCENTAGEM_TOTAL).toFixed(2)}%
                      </span>
                    </td>
                    {metodo !== 'PESO' && (
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
                          {c.icone} {c.rotulo}
                        </span>
                      </td>
                    )}
                    <td style={{ ...td, textAlign: 'center', whiteSpace: 'normal' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800,
                        background: reprovada ? '#fee2e2' : '#d1fae5', color: reprovada ? '#991b1b' : '#065f46',
                      }}>{a.STATUS || '—'}</span>
                      {/* Causa do IMPUR VISÍVEL (antes só existia como tooltip `title`,
                          invisível): é o que explica um 96% que reprova — questão
                          crítica "Não" ou % < 80%. O servidor manda pronto (T20). */}
                      {reprovada && a.MOTIVO_IMPUR && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#991b1b', lineHeight: 1.3, maxWidth: 220, marginLeft: 'auto', marginRight: 'auto' }}>
                          {a.MOTIVO_IMPUR}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button onClick={() => setDetalheId(a)} title="Ver resumo por área"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                          Detalhe
                        </button>
                        <button onClick={() => abrirPdf(a.ID, a.ORIGEM)} disabled={abrindoPdf === a.ID} title="Abrir o PDF (com as fotos das perguntas Não)"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: `1px solid ${COR}`, background: '#fff', color: COR, fontWeight: 700, fontSize: 12, cursor: abrindoPdf === a.ID ? 'wait' : 'pointer' }}>
                          {abrindoPdf === a.ID ? <Loader2 size={13} className="spin" /> : <FileText size={13} />}
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Página {paginaAtual} de {totalPaginas}
            <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {filtradas.length} avaliaç{filtradas.length === 1 ? 'ão' : 'ões'})</span>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPagina((p) => Math.max(p - 1, 1))} disabled={paginaAtual === 1}
              style={btnPag(paginaAtual === 1)}>Anterior</button>
            <button onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas}
              style={btnPag(paginaAtual === totalPaginas)}>Próxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const filtroLbl = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 };
const alerta = { padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 };
const btnPag = (desab) => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff',
  color: desab ? '#94a3b8' : '#0f172a', cursor: desab ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ---------------------------------------------------------------------
// Detalhe: RESULTADO GERAL + resumo por área. Pergunta a pergunta (com
// fotos) é o PDF — ver nota no topo do arquivo.
// ---------------------------------------------------------------------
const DetalheVisita = ({ registro, onVoltar, abrirPdf, abrindoPdf }) => {
  if (!registro) {
    return (
      <div>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ ...alerta, marginTop: 12 }}>Visita não encontrada nesta lista — atualize a página.</div>
      </div>
    );
  }

  const metodoNovo = !!registro.METODO_NOTA;
  const classif = metodoNovo ? classificarPorPercentual(registro.PORCENTAGEM_TOTAL) : null;
  const c = classif ? corClassificacao(classif) : { bg: '#f1f5f9', color: '#475569', icone: '⚪', rotulo: 'Método antigo' };
  const reprovada = String(registro.STATUS || '').trim() !== 'APROVADA';
  const areas = resumoPorArea(registro, metodoNovo);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Avaliação #{registro.ID} — {registro.LOJA} · {formatarDataBR(registro.DATA)}
        </h3>
        <button onClick={() => abrirPdf(registro.ID, registro.ORIGEM)} disabled={abrindoPdf === registro.ID} style={btnVermelho}>
          {abrindoPdf === registro.ID ? <Loader2 size={16} className="spin" /> : <FileText size={16} />} Abrir PDF
        </button>
      </div>

      <div style={{
        background: c.bg, border: `2px solid ${c.color}`,
        borderRadius: 12, padding: 16, marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 28 }}>{c.icone}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>RESULTADO GERAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>
            {Number(registro.PORCENTAGEM_TOTAL).toFixed(2)}% · {c.rotulo}
          </div>
          {/* Dois eixos, não um (T20): a cor mede o conjunto; o STATUS é o veto. */}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800,
              background: reprovada ? '#fee2e2' : '#d1fae5', color: reprovada ? '#991b1b' : '#065f46',
            }}>{registro.STATUS || '—'}</span>
            {reprovada && registro.MOTIVO_IMPUR && (
              <span style={{ fontSize: 13, color: '#991b1b' }}>{registro.MOTIVO_IMPUR}</span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <div>Método: <strong>{metodoNovo ? 'Contagem (novo)' : 'Peso (legado)'}</strong></div>
          <div>G. Negócios: <strong>{registro.GERENTE_NEGOCIO || '-'}</strong></div>
          <div>G. Plantão: <strong>{registro.GERENTE_PLANTAO || '-'}</strong></div>
          <div>Registrado por: <strong>{registro.REGISTRADO_POR || '-'}</strong></div>
        </div>
      </div>

      {/* Como se chega ao APROVADA/IMPUR: sinaliza as questões críticas e por que
          uma nota alta ainda reprova (pedido da Julia, 23/09 — a ANV deu IMPUR a
          96% por FS7). Só no método novo (Contagem): o set de críticas atual
          vale para a régua vigente; para o legado (Peso), fica a nota de método
          antigo do Resumo por Área. */}
      {/* Explicador do veto — só para o LEGADO em contagem: o set de 7 críticas
          é fixo daquela régua. No VERSIONADO as críticas variam por versão, então
          não afirmamos "7 críticas FS1…"; o MOTIVO_IMPUR (do servidor, acima) e o
          PDF trazem a causa correta daquela versão. */}
      {metodoNovo && registro.ORIGEM !== 'versionado' && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <ShieldAlert size={18} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              <strong>Como se chega ao {registro.STATUS || 'resultado'}.</strong> Além do percentual,
              a avaliação tem <strong>7 questões críticas</strong> —{' '}
              <strong>{CRITICAS_SA.join(', ')}</strong>. Um único <strong>"Não"</strong> em qualquer
              uma delas reprova a avaliação (<strong>IMPUR</strong>), por mais alto que seja o
              percentual. Percentual <strong>abaixo de 80%</strong> também reprova.
              {reprovada && registro.MOTIVO_IMPUR && (
                <> <span style={{ color: '#991b1b' }}>Nesta avaliação: <strong>{registro.MOTIVO_IMPUR}</strong>.</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resumo por área: só para as séries que trazem as respostas na linha
          (legado). O versionado não expõe P_* na lista — seu detalhe está no PDF
          (nota abaixo). */}
      {registro.ORIGEM !== 'versionado' && (
      <div style={card}>
        <h4 style={{ margin: '0 0 4px', color: '#0f172a' }}>Resumo por Área</h4>
        {!metodoNovo && (
          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#94a3b8' }}>
            Método antigo (peso): mostra a contagem de respostas, sem percentual — o peso por
            pergunta não está disponível na leitura (só no PDF, que preserva o cálculo original).
          </p>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: metodoNovo ? 12 : 0 }}>
          <thead style={{ background: '#475569' }}>
            <tr>
              <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'left' }}>Área</th>
              <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'center' }}>Sim</th>
              <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'center' }}>Não</th>
              <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'center' }}>N/A</th>
              {metodoNovo && <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'center' }}>%</th>}
              {metodoNovo && <th style={{ ...th2, color: '#fff', borderBottom: 'none', textAlign: 'center' }}>Classificação</th>}
            </tr>
          </thead>
          <tbody>
            {areas.map((ar) => {
              const ca = ar.classificacao ? corClassificacao(ar.classificacao) : null;
              return (
                <tr key={ar.area} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={td2}>{ar.area}</td>
                  <td style={{ ...td2, textAlign: 'center' }}>{ar.sim}</td>
                  <td style={{ ...td2, textAlign: 'center' }}>{ar.nao}</td>
                  <td style={{ ...td2, textAlign: 'center', color: '#94a3b8' }}>{ar.na}</td>
                  {metodoNovo && (
                    <td style={{ ...td2, textAlign: 'center', fontWeight: 700 }}>
                      {ar.classificacao === 'NA' ? '—' : `${ar.percentual}%`}
                    </td>
                  )}
                  {metodoNovo && (
                    <td style={{ ...td2, textAlign: 'center', background: ca?.bg, color: ca?.color, fontWeight: 700 }}>
                      {ca?.icone} {ca?.rotulo}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <div style={{ ...card, background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>
          O detalhe pergunta a pergunta — com as fotos de evidência das respostas "Não" — está no{' '}
          <strong>PDF</strong> desta visita (botão no topo).
        </p>
      </div>
    </div>
  );
};

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const th2 = { padding: 10, fontWeight: 800, whiteSpace: 'nowrap' };
const td2 = { padding: 10, color: '#0f172a' };
const btnVermelho = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default AvaliacaoSAHistorico;
